/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { SnakePath } from './SnakePath'
import {
    DndContext,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    closestCenter,
    DragOverlay,
    pointerWithin,
    MeasuringStrategy,
} from '@dnd-kit/core'
import { useState, useMemo } from 'react'
import { Aisle } from './Aisle'
import { WithdrawnZone } from './WithdrawnZone'
import { restrictToWindowEdges, snapCenterToCursor } from '@dnd-kit/modifiers'
import { AisleDragOverlay } from './AisleDragOverlay'
import { PositionNumber } from './PositionNumber'
import { useDeviceMediaQuery } from '../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { EmptySlot } from './EmptySlot'
import { DroppableZone } from './DroppableZone'


const DraggableList = ({pickingPath, withdrawnAisles: initialWithdrawnAisles = [], onChange }) => {
    const [aisles, setAisles] = useState(pickingPath)
    const [withdrawnAisles, setWithdrawnAisles] = useState(initialWithdrawnAisles)
    const [activeId, setActiveId] = useState(null)
    const [lastOverId, setLastOverId] = useState(null)
    const { isMobile, isTablet } = useDeviceMediaQuery()

    const sensors = useSensors(
      useSensor(MouseSensor, {
          activationConstraint: {
              distance: 5,
          },
      }),
      useSensor(TouchSensor, {
          activationConstraint: {
              delay: 250,
              tolerance: 5,
          },
      }),
    )

    const ITEMS_PER_ROW = useMemo(() => {
        if (isMobile) return 1
        if (isTablet) return 3
        return 5
    }, [isMobile, isTablet])

    const fixedPositions = useMemo(() => {
        const totalItems = Math.max(aisles.length, 12)
        const totalRows = Math.ceil(totalItems / ITEMS_PER_ROW)
        const positions = []

        for (let row = 0; row < totalRows; row++) {
            const isEvenRow = row % 2 === 0

            for (let col = 0; col < ITEMS_PER_ROW; col++) {
                let positionNumber

                if (isEvenRow) {
                    positionNumber = row * ITEMS_PER_ROW + col + 1
                } else {
                    positionNumber = (row + 1) * ITEMS_PER_ROW - col
                }

                positions.push(positionNumber)
            }
        }

        return positions
    }, [aisles.length, ITEMS_PER_ROW])

    const gridLayout = useMemo(() => {
        const totalItems = aisles.length
        const totalRows = Math.ceil(totalItems / ITEMS_PER_ROW)

        const cells = []
        let remainingItems = totalItems

        for (let row = 0; row < totalRows; row++) {
            const isEvenRow = row % 2 === 0
            const itemsInThisRow = Math.min(remainingItems, ITEMS_PER_ROW)
            const emptySlotsInThisRow = ITEMS_PER_ROW - itemsInThisRow

            for (let column = 0; column < ITEMS_PER_ROW; column++) {
                const position = row * ITEMS_PER_ROW + column
                const positionNumber = fixedPositions[position]

                let isItemPosition

                if (isEvenRow) {
                    isItemPosition = column < itemsInThisRow
                } else {
                    isItemPosition = column >= emptySlotsInThisRow
                }

                if (isItemPosition && remainingItems > 0) {
                    const itemIndex = totalItems - remainingItems

                    cells.push({
                        type: 'item',
                        aisle: aisles[itemIndex],
                        position: positionNumber,
                        visualRow: row,
                        visualCol: column,
                        cellIndex: position,
                        arrayIndex: itemIndex,
                    })

                    remainingItems--
                } else {
                    cells.push({
                        type: 'empty',
                        id: `empty-${position}`,
                        position: positionNumber,
                        visualRow: row,
                        visualCol: column,
                        cellIndex: position,
                    })
                }
            }
        }

        return cells
    }, [aisles, fixedPositions, ITEMS_PER_ROW])

    const handleOnDragStart = (event) => {
        setActiveId(event.active.id)
    }

    const handleOnDragOver = (event) => {
        const {over} = event
        if (!over) return
        setLastOverId(over.id)
    }

    const handleOnDragEnd = (event) => {
        setActiveId(null)
        const {active, over} = event

        if (!over) {
            return
        }

        const effectiveOverId = over.id || lastOverId

        const isFromMainZone = aisles.some(item => item.id === active.id)
        const isToWithdrawnZone = effectiveOverId === 'withdrawn-zone' || withdrawnAisles.some(item => item.id === effectiveOverId)
        const isToMainZone = effectiveOverId === 'main-zone' || aisles.some(item => item.id === effectiveOverId)

        // Handle moving from main zone to withdrawn zone
        if (isFromMainZone && isToWithdrawnZone) {
            const item = aisles.find(item => item.id === active.id)
            // Remove the item from the main zone
            const newAisles = aisles.filter(item => item.id !== active.id)
                .map((item, idx) => ({...item, position: idx}))

            // Add it to the withdrawn zone
            const newWithdrawnAisles = [...withdrawnAisles, item]
                .map((item, idx) => ({...item, position: idx}))

            setAisles(newAisles)
            setWithdrawnAisles(newWithdrawnAisles)
            onChange(newAisles)
            return
        }

        // Handle moving from withdrawn zone to main zone
        if (!isFromMainZone && isToMainZone) {
            const item = withdrawnAisles.find(item => item.id === active.id)
            const newWithdrawnAisles = withdrawnAisles.filter(item => item.id !== active.id)
                .map((item, idx) => ({...item, position: idx}))

            let newAisles
            if (effectiveOverId !== 'main-zone') {
                const overIndex = aisles.findIndex(item => item.id === effectiveOverId)
                newAisles = [
                    ...aisles.slice(0, overIndex),
                    item,
                    ...aisles.slice(overIndex),
                ].map((item, idx) => ({...item, position: idx}))
            } else {
                newAisles = [...aisles, item]
                    .map((item, idx) => ({...item, position: idx}))
            }

            setAisles(newAisles)
            setWithdrawnAisles(newWithdrawnAisles)
            onChange(newAisles, newWithdrawnAisles)
            return
        }

        // Handle reordering within the main zone
        if (isFromMainZone && isToMainZone && active.id !== effectiveOverId && effectiveOverId !== 'main-zone') {
            const activeCell = gridLayout.find(cell => cell.type === 'item' && cell.aisle.id === active.id)
            const overCell = gridLayout.find(cell => cell.type === 'item' && cell.aisle.id === effectiveOverId)

            if (activeCell && overCell) {
                const activeIndex = aisles.findIndex(item => item.id === active.id)
                const overIndex = aisles.findIndex(item => item.id === effectiveOverId)

                const newAisles = [...aisles]
                const [movedItem] = newAisles.splice(activeIndex, 1)
                newAisles.splice(overIndex, 0, movedItem)

                const finalAisles = newAisles.map((item, idx) => ({...item, position: idx}))
                setAisles(finalAisles)
                onChange(finalAisles, withdrawnAisles)
            }
            return
        }

        // Handle reordering within the withdrawn zone
        if (!isFromMainZone && isToWithdrawnZone &&
            active.id !== effectiveOverId && effectiveOverId !== 'withdrawn-zone') {
            const oldIndex = withdrawnAisles.findIndex(item => item.id === active.id)
            const newIndex = withdrawnAisles.findIndex(item => item.id === effectiveOverId)
            const sortedItems = arrayMove(withdrawnAisles, oldIndex, newIndex)
                .map((item, idx) => ({...item, position: idx}))

            setWithdrawnAisles(sortedItems)
            onChange(aisles, sortedItems)
            return
        }
    }

    const handleOnAisleChange = (aisleId, newAisles) => {
        const updatedAisles = aisles.map(aisle => {
            if (aisle.id === aisleId) {
                return {
                    ...aisle,
                    aisles: newAisles,
                }
            }
            return aisle
        })

        setAisles(updatedAisles)
        onChange(updatedAisles, withdrawnAisles)
    }

    const handleWithdrawnAislesChange = (newWithdrawnAisles) => {
        setWithdrawnAisles(newWithdrawnAisles)
        onChange(aisles, newWithdrawnAisles)
    }

    const handleMoveToWithdrawn = (aisleId) => {
        const item = aisles.find(item => item.id === aisleId)

        if (item) {
            const newAisles = aisles.filter(item => item.id !== aisleId)
                .map((item, idx) => ({...item, position: idx}))

            const newWithdrawnAisles = [...withdrawnAisles, item]
                .map((item, idx) => ({...item, position: idx}))

            setAisles(newAisles)
            setWithdrawnAisles(newWithdrawnAisles)
            onChange(newAisles, newWithdrawnAisles)
        }
    }

    const activeItem = activeId ? [...aisles, ...withdrawnAisles].find(item => item.id === activeId) : null

    const collisionDetectionStrategy = (args) => {
        if (args.active.id && aisles.some(item => item.id === args.active.id)) {
            const withdrawnZoneCollisions = args.droppableContainers
              .filter(container => container.id === 'withdrawn-zone')
              .filter(container => {
                  return pointerWithin({
                      ...args,
                      droppableContainers: [container],
                  }).length > 0
              })

            if (withdrawnZoneCollisions.length > 0) {
                return withdrawnZoneCollisions
            }
        }

        const pointerCollisions = pointerWithin(args)
        if (pointerCollisions.length > 0) {
            return pointerCollisions
        }

        return closestCenter(args)
    }

    const sortableAisles = useMemo(() => {
        return aisles.map(aisle => ({id: aisle.id}))
    }, [aisles])

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleOnDragStart}
        onDragOver={handleOnDragOver}
        onDragEnd={handleOnDragEnd}
        modifiers={[restrictToWindowEdges, snapCenterToCursor]}
        measuring={{
            droppable: {
                strategy: MeasuringStrategy.Always,
            },
        }}
      >
          <div
              className="draggable-list-container"
              css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 5rem;
                  position: relative;
              `}>
              <div css={css`
                  display: flex;
                  position: relative;
                  width: 100%;
              `}>
                  <div css={css`
                      display: flex;
                      flex-direction: column;
                      width: 100%;
                      position: relative;
                  `}>
                      <DroppableZone
                        id="main-zone"
                        css={css`
                            border-radius: 5px;
                            min-height: 200px;
                            width: 100%;
                            position: relative;
                            padding: 1rem;
                        `}
                      >
                          <SnakePath aisles={aisles} />

                          <SortableContext
                            items={sortableAisles}
                            strategy={rectSortingStrategy}
                          >
                              <div css={css`
                                  display: grid;
                                  grid-template-columns: repeat(${ITEMS_PER_ROW}, 1fr);
                                  align-items: center;
                                  gap: 1rem;
                                  width: 100%;
                              `}>
                                  {gridLayout.map((cell) => {
                                      if (cell.type === 'empty') {
                                          return <EmptySlot key={cell.id} />
                                      } else {
                                          return (
                                            <div
                                              key={cell.aisle.id}
                                              className="grid-item"
                                              css={css`
                                                  position: relative;
                                                  z-index: 1;
                                              `}
                                            >
                                                <PositionNumber position={cell.position}/>
                                                <div css={css`width: 100%;`}>
                                                    <Aisle
                                                      id={cell.aisle.id}
                                                      aisle={cell.aisle}
                                                      onChange={newAisles => handleOnAisleChange(cell.aisle.id, newAisles)}
                                                      onMoveToWithdrawn={handleMoveToWithdrawn}
                                                    />
                                                </div>
                                            </div>
                                          )
                                      }
                                  })}
                              </div>
                          </SortableContext>
                      </DroppableZone>
                  </div>
              </div>

            <WithdrawnZone
              withdrawnAisles={withdrawnAisles}
              onChange={handleWithdrawnAislesChange}
            />
          </div>

          <DragOverlay zIndex={10}>
              {activeId && activeItem ? (
                <AisleDragOverlay
                  aisle={activeItem}
                />
              ) : null}
          </DragOverlay>
      </DndContext>
    )
}

export { DraggableList }
